<template>
  <div :class="$options.name" :data-e-ga-id="uniqueId">
    <div v-if="$slots.default" :id="tooltipUniqueId">
      <!-- @slot For the element to trigger the tooltip  -->
      <slot />
    </div>
    <span v-else :id="tooltipUniqueId" :class="`${$options.name}__icon_container`">
      <font-awesome-icon :class="`${$options.name}__icon_container__icon tw:p-1`" icon="fa-light fa-circle-question" />
    </span>
    <div v-if="type === 'custom'" ref="custom" :class="`${$options.name}__custom`">
      <!-- @slot For the content to display inside the tooltip instead of helper -->
      <slot name="custom" />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, onUnmounted, ref, watch } from "vue";
import type { PropType } from "vue";
import tippy, { animateFill, hideAll } from "tippy.js";
import type { Instance, Props } from "tippy.js";
import "tippy.js/dist/backdrop.css";
import "tippy.js/animations/shift-away.css";
import { gtagUniversalSend } from "vue-app/plugins/edroneGtag";

const COMPONENT_NAME = "EdroneTooltip";

export default defineComponent({
  name: "EdroneTooltip",
  props: {
    /**
     * Tooltip text
     */
    helper: {
      type: String,
      required: false,
      default: "",
    },
    /**
     * Variants of tooltip
     * - If "normal" is set, the tooltip will be shown at the bottom of the element - used for one-word hints.
     * - If "custom" is set, the tooltip will be shown on click, at the bottom-end of the element - used for custom content provided by slot.
     */
    type: {
      type: String as PropType<"normal" | "custom">,
      required: false,
      default: "normal",
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    customTheme: {
      type: String,
      required: false,
      default: "",
    },
    /**
     * Class name that trigger closing tooltip after clicking element that has this class name.
     * When this prop is not provided, then clicking anywhere inside the tooltip will close it.
     */
    closeTriggerClassName: {
      type: String,
      required: false,
      default: "",
    },
    position: {
      type: String as PropType<"top" | "bottom" | "left" | "right" | "bottom-start" | "right-start" | "bottom-end">,
      required: false,
      default: "top",
    },
    /**
     * Event that triggers custom tooltip.
     * You can provide any event that would trigger opening the tooltip, for example: "mouseenter" or "click".
     * For more info go to:
     * - https://atomiks.github.io/tippyjs/v6/all-props/#trigger
     * - https://atomiks.github.io/tippyjs/#triggers
     */
    triggerEvent: {
      type: String as PropType<"click" | "mouseenter" | "mouseenter click">,
      required: false,
      default: "click",
    },
    /**
     * Max width of the tooltip in px. Default is 320px.
     */
    maxWidth: {
      type: String,
      required: false,
      default: "320px",
    },
    /**
     * Offset of the custom tooltip from the element.
     * For more info go to: https://atomiks.github.io/tippyjs/v6/all-props/#offset
     * Example: [0, 10] - will move the tooltip 10px down.
     */
    offset: {
      type: Array as unknown as PropType<[number, number]>,
      required: false,
      default: () => [0, 2],
    },

    /**
     * When true, show tooltip when it's been created. Needed for Storybook.
     * For more info go to: https://atomiks.github.io/tippyjs/v6/all-props/#showoncreate
     */
    show: {
      type: Boolean,
      required: false,
      default: false,
    },
    uniqueId: {
      type: String,
      required: false,
      default: "",
    },
  },
  emits: ["close", "open"],
  setup(props, { emit }) {
    const tooltipUniqueId = ref(`${COMPONENT_NAME}${Math.ceil(Math.random() * 1000000)}`);

    const watchHintPropertyChange = (instance: Instance<Props>[]) => {
      watch(
        () => props.helper,
        (updatedProp) => {
          if (instance[0]) {
            instance[0].setContent(updatedProp);
          }
        }
      );
    };

    const watchPositionPropertyChange = (instance: Instance<Props>[]) => {
      watch(
        () => props.position,
        (updatedProp) => {
          if (instance[0]) {
            instance[0].setProps({
              placement: updatedProp,
            });
          }
        }
      );
    };

    const watchTriggerEventPropertyChange = (instance: Instance<Props>[]) => {
      watch(
        () => props.triggerEvent,
        (updatedProp) => {
          if (instance[0]) {
            instance[0].setProps({
              trigger: updatedProp,
            });
          }
        }
      );
    };

    const watchDisabledPropertyChange = (instance: Instance<Props>[]) => {
      if (!instance[0]) return;
      props.disabled ? instance[0].disable() : instance[0].enable();
      watch(
        () => props.disabled,
        (updatedProp) => {
          if (instance[0]) {
            if (updatedProp) {
              instance[0].disable();
            } else {
              instance[0].enable();
            }
          }
        }
      );
    };

    const custom = ref(null);

    onMounted(() => {
      let instance: Instance<Props>[] | Instance<Props> = [];
      const tooltipContent = custom.value as unknown as HTMLElement;

      if (props.type === "normal") {
        instance = tippy("#" + tooltipUniqueId.value, {
          theme: "normal",
          content: props.helper,
          placement: props.position,
          trigger: props.triggerEvent,
          maxWidth: props.maxWidth,
          appendTo: () => document.body,
          animateFill: true,
          interactive: true,
          arrow: false,
          offset: props.offset,
          showOnCreate: props.show,
          zIndex: 99999,
          plugins: [animateFill],
        });
      }
      if (props.type === "custom") {
        const click = (e: MouseEvent) => {
          if (props.closeTriggerClassName) {
            const targetClasses = (JSON.stringify((e?.target as HTMLElement)?.className) ||
              JSON.stringify((e?.target as HTMLElement)?.classList)) as string;
            if (targetClasses.includes(props.closeTriggerClassName)) {
              (instance as unknown as Instance<Props>[])[0].hide();
            }
          } else if ((e.target as HTMLElement).closest(".edrone-tooltip__custom")) {
            (instance as unknown as Instance<Props>[])[0].hide();
          }
        };
        const close = () => {
          emit("close");
        };

        const open = () => {
          emit("open");
          gtagUniversalSend({ component_id: props.uniqueId });
        };

        instance = tippy("#" + tooltipUniqueId.value, {
          theme: props.customTheme ? props.customTheme : "custom",
          content: tooltipContent,
          placement: props.position,
          zIndex: 99999,
          maxWidth: props.maxWidth ? props.maxWidth : "320px",
          appendTo: () => document.body,
          animateFill: true,
          trigger: props.triggerEvent,
          interactive: true,
          arrow: false,
          showOnCreate: props.show,
          offset: props.offset,
          plugins: [animateFill],
          onShow() {
            document.addEventListener("click", click);
            open();
          },
          onHide() {
            document.removeEventListener("click", click);
            close();
          },
          onTrigger(instance, event) {
            event.stopPropagation();
          },
          onUntrigger(instance, event) {
            event.stopPropagation();
          },
        });
      }

      watchHintPropertyChange(instance);
      watchPositionPropertyChange(instance);
      watchTriggerEventPropertyChange(instance);
      watchDisabledPropertyChange(instance);
    });

    onUnmounted(() => {
      hideAll({ duration: 0 });
    });

    return {
      tooltipUniqueId,
      custom,
    };
  },
});
</script>

<style>
@reference "vue-app/assets/scss/main.css";

.tippy-box[data-animation="fade"][data-state="hidden"] {
  opacity: 0;
}
[data-tippy-root] {
  max-width: calc(100vw - 10px);
}
.tippy-box {
  position: relative;
  background-color: #333;
  border-radius: 4px;
  font-size: 14px;
  line-height: 1.4;
  white-space: normal;
  outline: 0;
  transition-property: transform, visibility, opacity;
  font-family: "Roboto", sans-serif;
}
.tippy-box[data-placement^="top"] > .tippy-arrow {
  bottom: 0;
}
.tippy-box[data-placement^="top"] > .tippy-arrow:before {
  bottom: -7px;
  left: 0;
  border-width: 8px 8px 0;
  border-top-color: initial;
  transform-origin: center top;
}
.tippy-box[data-placement^="bottom"] > .tippy-arrow {
  top: 0;
}
.tippy-box[data-placement^="bottom"] > .tippy-arrow:before {
  top: -7px;
  left: 0;
  border-width: 0 8px 8px;
  border-bottom-color: initial;
  transform-origin: center bottom;
}
.tippy-box[data-placement^="left"] > .tippy-arrow {
  right: 0;
}
.tippy-box[data-placement^="left"] > .tippy-arrow:before {
  border-width: 8px 0 8px 8px;
  border-left-color: initial;
  right: -7px;
  transform-origin: center left;
}
.tippy-box[data-placement^="right"] > .tippy-arrow {
  left: 0;
}
.tippy-box[data-placement^="right"] > .tippy-arrow:before {
  left: -7px;
  border-width: 8px 8px 8px 0;
  border-right-color: initial;
  transform-origin: center right;
}
.tippy-box[data-inertia][data-state="visible"] {
  transition-timing-function: cubic-bezier(0.54, 1.5, 0.38, 1.11);
}
.tippy-arrow {
  width: 16px;
  height: 16px;
  color: #333;
}
.tippy-arrow:before {
  content: "";
  position: absolute;
  border-color: transparent;
  border-style: solid;
}
.tippy-content {
  position: relative;
  padding: 5px 9px;
  z-index: 1;
}

.tippy-backdrop {
  @apply tw:bg-white;
}

.tippy-box[data-theme~="normal"][data-placement^="top"] > .tippy-arrow::before {
  @apply tw:border-t-white;
}
.tippy-box[data-theme~="normal"][data-placement^="bottom"] > .tippy-arrow::before {
  @apply tw:border-b-white;
}
.tippy-box[data-theme~="normal"][data-placement^="left"] > .tippy-arrow::before {
  @apply tw:border-l-white;
}
.tippy-box[data-theme~="normal"][data-placement^="right"] > .tippy-arrow::before {
  @apply tw:border-r-white;
}

.tippy-box[data-theme~="normal"] {
  @apply tw:caption;
  @apply tw:bg-white;
  box-shadow: 4px 4px 24px rgba(0, 0, 0, 0.22);
  margin-bottom: 0;
}

.tippy-box[data-theme~="normal"] .tippy-content {
  @apply tw:p-2;
  text-wrap: balance;
}

.tippy-box[data-theme~="custom"] {
  @apply tw:bg-white;
  box-shadow: 4px 4px 24px rgba(0, 0, 0, 0.22);
}

.tippy-box[data-theme~="custom"] .tippy-content {
  @apply tw:p-2;
  @apply tw:bg-white;
}

.EdroneTooltip {
  width: fit-content;
}

.EdroneTooltip__icon_container {
  display: flex;
  height: 24px;
  width: fit-content;
}

.EdroneTooltip__icon_container__icon {
  @apply tw:text-default-500;
  cursor: pointer;
  font-size: 1rem;
  border-radius: 50%;
  transition: all 0.2s ease-in-out;
}

.EdroneTooltip__icon_container__icon:hover {
  @apply tw:text-default-600 tw:bg-default-100;
}
</style>
